// Image processing utilities (Canvas-based)
import { estimateBackgroundColor } from './autoDetect';

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Render a sheet image to a hidden canvas and return ImageData
export function getImageData(img) {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  return { data: ctx.getImageData(0, 0, canvas.width, canvas.height), canvas, ctx };
}

// Crop a region of the source image and return a canvas with optional bg removal + shape mask
// shape: 'rect' | 'circle' | 'rounded' | 'square' | 'polygon-N' | 'heritage' | 'dino'
// bgRemove: boolean
// bgTolerance: 0-100
// aggressive: boolean — extra removal pass for noisy backgrounds
// centerInShape: boolean — when true, fit the (bg-removed, trimmed) image inside the
//   shape's inscribed rectangle so the whole subject is visible with transparent
//   letterbox around it. When false (default) the image fills the entire shape
//   envelope and may be clipped by curved edges.
// border: { enabled, color, thickness } — optional outline stroke along the shape edge.
export function cropRegion(img, box, opts = {}) {
  const {
    shape = 'rect',
    bgRemove = true,
    bgTolerance = 30,
    bgColor = null,
    edgeFeather = 1,
    pad = 4,
    aggressive = false,
    centerInShape = false,
    border = null,
    bgFill = null, // hex color or null — when set, paints solid color behind transparent areas
  } = opts;

  const x = Math.max(0, Math.floor(box.x - pad));
  const y = Math.max(0, Math.floor(box.y - pad));
  const w = Math.min(img.naturalWidth - x, Math.ceil(box.w + pad * 2));
  const h = Math.min(img.naturalHeight - y, Math.ceil(box.h + pad * 2));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);

  // Background removal — sample bg from edges of THIS crop or use given bgColor
  if (bgRemove) {
    const imgData = ctx.getImageData(0, 0, w, h);
    // Build list of bg color candidates: passed bgColor + each corner sample
    const candidates = [];
    if (bgColor) candidates.push(bgColor);
    candidates.push(estimateBackgroundColor(imgData.data, w, h));
    if (aggressive) {
      // also sample each corner separately for multi-tone backgrounds
      candidates.push(...sampleCornersIndividually(imgData.data, w, h));
    }
    const tolSum = bgTolerance * 3;
    const featherSum = tolSum + Math.max(0, edgeFeather) * 30;
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      let minD = Infinity;
      for (const bg of candidates) {
        const d = Math.abs(data[i] - bg.r) + Math.abs(data[i + 1] - bg.g) + Math.abs(data[i + 2] - bg.b);
        if (d < minD) minD = d;
      }
      if (minD <= tolSum) {
        data[i + 3] = 0;
      } else if (minD < featherSum) {
        const t = (minD - tolSum) / (featherSum - tolSum);
        data[i + 3] = Math.round(data[i + 3] * t);
      }
    }
    ctx.putImageData(imgData, 0, 0);

    if (aggressive) {
      // Flood-fill from each corner that's still opaque, removing connected bg
      floodFillRemove(ctx, w, h, bgTolerance);
    }

    // Trim transparent borders for tighter PNG
    const trimmed = trimTransparent(canvas);
    if (trimmed) return fillTransparentBg(applyShape(trimmed, shape, { centerInShape, border }), bgFill);
  }
  return fillTransparentBg(applyShape(canvas, shape, { centerInShape, border }), bgFill);
}

// Paint a solid background behind any remaining transparent pixels.
// Uses destination-over so existing opaque pixels stay on top untouched.
// Returns the same canvas (mutated) when fillColor is set, or unchanged otherwise.
function fillTransparentBg(canvas, fillColor) {
  if (!fillColor || !canvas) return canvas;
  const ctx = canvas.getContext('2d');
  ctx.save();
  ctx.globalCompositeOperation = 'destination-over';
  ctx.fillStyle = fillColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.restore();
  return canvas;
}

function sampleCornersIndividually(data, W, H) {
  const patch = Math.max(3, Math.floor(Math.min(W, H) * 0.05));
  const corners = [
    [0, 0], [W - patch, 0], [0, H - patch], [W - patch, H - patch],
  ];
  const out = [];
  for (const [cx, cy] of corners) {
    let r = 0, g = 0, b = 0, n = 0;
    for (let y = cy; y < cy + patch && y < H; y++) {
      for (let x = cx; x < cx + patch && x < W; x++) {
        const i = (y * W + x) * 4;
        if (data[i + 3] < 32) continue;
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
      }
    }
    if (n > 0) out.push({ r: Math.round(r / n), g: Math.round(g / n), b: Math.round(b / n) });
  }
  return out;
}

function floodFillRemove(ctx, W, H, tolerance) {
  // remove background by flooding from any corner pixel still opaque
  const imgData = ctx.getImageData(0, 0, W, H);
  const data = imgData.data;
  const visited = new Uint8Array(W * H);
  const starts = [0, W - 1, (H - 1) * W, H * W - 1];
  const stack = [];
  for (const s of starts) {
    if (data[s * 4 + 3] > 16) stack.push(s);
  }
  while (stack.length) {
    const p = stack.pop();
    if (visited[p]) continue;
    visited[p] = 1;
    const i = p * 4;
    if (data[i + 3] < 16) continue;
    // tolerance check against neighbors' colors handled by checking similarity
    const r = data[i], g = data[i + 1], b = data[i + 2];
    data[i + 3] = 0;
    const px = p % W;
    const py = (p - px) / W;
    const neighbors = [];
    if (px > 0) neighbors.push(p - 1);
    if (px < W - 1) neighbors.push(p + 1);
    if (py > 0) neighbors.push(p - W);
    if (py < H - 1) neighbors.push(p + W);
    for (const n of neighbors) {
      if (visited[n]) continue;
      const j = n * 4;
      if (data[j + 3] < 16) continue;
      const d = Math.abs(data[j] - r) + Math.abs(data[j + 1] - g) + Math.abs(data[j + 2] - b);
      if (d <= tolerance * 3) stack.push(n);
    }
  }
  ctx.putImageData(imgData, 0, 0);
}

function trimTransparent(canvas) {
  const ctx = canvas.getContext('2d');
  const { width: w, height: h } = canvas;
  const { data } = ctx.getImageData(0, 0, w, h);
  let top = h, bottom = 0, left = w, right = 0, found = false;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a > 8) {
        found = true;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  if (!found) return null;
  const newW = right - left + 1;
  const newH = bottom - top + 1;
  if (newW === w && newH === h) return canvas;
  const out = document.createElement('canvas');
  out.width = newW;
  out.height = newH;
  out.getContext('2d').drawImage(canvas, left, top, newW, newH, 0, 0, newW, newH);
  return out;
}

function applyShape(canvas, shape, opts = {}) {
  const { centerInShape = false, border = null } = opts;
  if (shape === 'rect') {
    // Even for rect, support a simple inset border if requested
    if (border?.enabled) {
      const out = document.createElement('canvas');
      out.width = canvas.width; out.height = canvas.height;
      const ctx = out.getContext('2d');
      ctx.drawImage(canvas, 0, 0);
      const t = Math.max(1, border.thickness || 4);
      ctx.lineWidth = t;
      ctx.strokeStyle = border.color || '#1f3d2b';
      ctx.strokeRect(t / 2, t / 2, out.width - t, out.height - t);
      return out;
    }
    return canvas;
  }

  const { width: srcW, height: srcH } = canvas;

  // When centerInShape is true, render onto a SQUARE canvas with the subject
  // scaled to fit within the shape's inscribed rectangle (or inscribed circle
  // for circle/heritage/dino), surrounded by transparent letterbox.
  // Inscribed-rectangle factors (relative to shape's bounding box):
  const inscribed = {
    circle: 0.707,         // inscribed square in a circle
    heritage: 0.78,        // wavy circle — slightly more usable area than plain circle
    dino: 0.55,            // dino silhouette has a narrow body, so be conservative
    rounded: 0.94,         // rounded rect — most of the box is usable
    square: 1.0,
    'polygon-3': 0.45,
    'polygon-4': 0.72,
    'polygon-5': 0.76,
    'polygon-6': 0.86,
    'polygon-7': 0.88,
    'polygon-8': 0.92,
    'polygon-9': 0.93,
    'polygon-10': 0.94,
    'polygon-11': 0.95,
    'polygon-12': 0.96,
  }[shape] ?? 0.85;

  let workCanvas = canvas;
  let w = srcW, h = srcH;

  if (centerInShape) {
    const side = Math.max(srcW, srcH);
    workCanvas = document.createElement('canvas');
    workCanvas.width = side;
    workCanvas.height = side;
    const wctx = workCanvas.getContext('2d');
    // Compute scale so the source fits within inscribed area
    const innerSide = side * inscribed;
    const scale = Math.min(innerSide / srcW, innerSide / srcH);
    const drawW = srcW * scale;
    const drawH = srcH * scale;
    const dx = (side - drawW) / 2;
    const dy = (side - drawH) / 2;
    wctx.drawImage(canvas, 0, 0, srcW, srcH, dx, dy, drawW, drawH);
    w = side; h = side;
  }

  // Image-based masks (heritage scalloped, dino silhouette)
  if (shape === 'heritage' || shape === 'dino') {
    return applyImageMask(workCanvas, shape, border);
  }

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  const buildPath = (c) => {
    c.beginPath();
    if (shape === 'circle') {
      const r = Math.min(w, h) / 2;
      c.arc(w / 2, h / 2, r, 0, Math.PI * 2);
    } else if (shape === 'rounded') {
      const r = Math.min(w, h) * 0.18;
      roundedRect(c, 0, 0, w, h, r);
    } else if (shape === 'square') {
      const s = Math.min(w, h);
      const ox = (w - s) / 2;
      const oy = (h - s) / 2;
      c.rect(ox, oy, s, s);
    } else if (shape && shape.startsWith('polygon-')) {
      const sides = parseInt(shape.slice('polygon-'.length), 10);
      if (sides >= 3 && sides <= 12) drawRegularPolygon(c, w / 2, h / 2, Math.min(w, h) / 2, sides);
      else c.rect(0, 0, w, h);
    } else {
      c.rect(0, 0, w, h);
    }
    c.closePath();
  };
  ctx.save();
  buildPath(ctx);
  ctx.clip();
  ctx.drawImage(workCanvas, 0, 0);
  ctx.restore();

  if (border?.enabled) {
    const t = Math.max(1, border.thickness || 4);
    ctx.save();
    ctx.lineWidth = t;
    ctx.strokeStyle = border.color || '#1f3d2b';
    buildPath(ctx);
    ctx.stroke();
    ctx.restore();
  }
  return out;
}

function drawRegularPolygon(ctx, cx, cy, r, sides) {
  // Rotate so flat side (or single vertex) sits naturally at the top.
  const rotation = -Math.PI / 2;
  for (let i = 0; i < sides; i++) {
    const a = rotation + (i * 2 * Math.PI) / sides;
    const x = cx + r * Math.cos(a);
    const y = cy + r * Math.sin(a);
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
}

// Cache mask images so we only fetch them once.
const _maskCache = {};
function loadMaskImage(shape) {
  if (_maskCache[shape]) return _maskCache[shape];
  const src = shape === 'heritage' ? '/assets/heritage-mask.png' : '/assets/dino-mask.png';
  const img = new Image();
  img.src = src;
  _maskCache[shape] = img;
  return img;
}

// Preload masks at module load so the first crop call doesn't stall.
if (typeof window !== 'undefined') {
  loadMaskImage('heritage');
  loadMaskImage('dino');
}

function applyImageMask(canvas, shape, border) {
  const { width: w, height: h } = canvas;
  const mask = loadMaskImage(shape);

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');

  ctx.drawImage(canvas, 0, 0);

  if (mask.complete && mask.naturalWidth > 0) {
    const s = Math.min(w, h);
    const ox = (w - s) / 2;
    const oy = (h - s) / 2;
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(mask, ox, oy, s, s);
    ctx.globalCompositeOperation = 'source-over';

    if (border?.enabled) {
      // Draw the mask in border colour, then keep only its silhouette edge by
      // first painting a slightly-smaller mask in destination-out (the inner)
      // so what remains is a hollow outline along the mask edge.
      const t = Math.max(1, border.thickness || 4);
      const ring = document.createElement('canvas');
      ring.width = w; ring.height = h;
      const rctx = ring.getContext('2d');
      rctx.fillStyle = border.color || '#1f3d2b';
      rctx.fillRect(0, 0, w, h);
      // Keep only inside the mask
      rctx.globalCompositeOperation = 'destination-in';
      rctx.drawImage(mask, ox, oy, s, s);
      // Carve out an inner mask (shrunk by 2t) to leave only the rim
      rctx.globalCompositeOperation = 'destination-out';
      rctx.drawImage(mask, ox + t, oy + t, s - t * 2, s - t * 2);
      rctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(ring, 0, 0);
    }
  } else {
    ctx.globalCompositeOperation = 'destination-in';
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    if (!mask._onloadHooked) {
      mask._onloadHooked = true;
      mask.addEventListener('load', () => {
        window.dispatchEvent(new CustomEvent('cropasaurus-mask-loaded', { detail: { shape } }));
      });
    }
  }
  return out;
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
}

export function canvasToBlob(canvas, mime = 'image/png', quality = 0.95) {
  return new Promise(resolve => canvas.toBlob(b => resolve(b), mime, quality));
}

// For JPEG export with white background (since JPEG has no alpha)
export function canvasToJpegBlob(canvas, quality = 0.92, bg = '#ffffff') {
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = canvas.height;
  const ctx = out.getContext('2d');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, 0);
  return canvasToBlob(out, 'image/jpeg', quality);
}

export function formatName(prefix, index, padding, total) {
  const safePrefix = (prefix || '').trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, '_');
  const minPad = Math.max(padding, String(total).length);
  const num = String(index).padStart(minPad, '0');
  return safePrefix ? `${safePrefix}_${num}` : `${num}`;
}
